// ShopOnboarding.js - Shop Owner Payment Setup
// Integrates with Square for subscription management

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PaymentForm, CreditCard } from 'react-square-web-payments-sdk';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from './App';
import { useAuth } from './App';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';

// Square Application ID (from Square Developer Dashboard)
const SQUARE_APP_ID = process.env.REACT_APP_SQUARE_APP_ID || 'YOUR_SQUARE_APP_ID';
const SQUARE_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID || 'YOUR_LOCATION_ID';

const ShopOnboarding = () => {
  const navigate = useNavigate();
  const { user, shopId, shopData } = useAuth();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Business info form
  const [businessInfo, setBusinessInfo] = useState({
    businessName: shopData?.shopName || '',
    ownerName: '',
    email: shopData?.ownerEmail || user?.email || '',
    phone: shopData?.ownerPhone || ''
  });

  // If shop is already active, redirect to dashboard
  useEffect(() => {
    if (shopData?.status === 'active' && shopData?.squareSubscriptionId) {
      navigate('/dashboard');
    }
  }, [shopData, navigate]);

  const handleBusinessInfoChange = (e) => {
    const { name, value } = e.target;
    setBusinessInfo(prev => ({ ...prev, [name]: value }));
  };

  // Handle successful card tokenization
  const handleCardNonce = async (token) => {
    setLoading(true);
    setError(null);

    try {
      // Call Cloud Function to create Square customer and subscription
      const createSubscription = httpsCallable(functions, 'createSquareSubscription');
      
      const result = await createSubscription({
        shopId: shopId,
        cardNonce: token.token,
        customerInfo: {
          givenName: businessInfo.ownerName.split(' ')[0],
          familyName: businessInfo.ownerName.split(' ').slice(1).join(' '),
          emailAddress: businessInfo.email,
          phoneNumber: businessInfo.phone,
          companyName: businessInfo.businessName
        }
      });

      if (result.data.success) {
        // Update local shop document with Square IDs
        await updateDoc(doc(db, 'shops', shopId), {
          status: 'active',
          squareCustomerId: result.data.customerId,
          squareSubscriptionId: result.data.subscriptionId,
          subscriptionStartedAt: serverTimestamp(),
          ownerName: businessInfo.ownerName,
          ownerEmail: businessInfo.email,
          ownerPhone: businessInfo.phone
        });

        setSuccess(true);
      } else {
        throw new Error(result.data.error || 'Failed to create subscription');
      }
    } catch (err) {
      console.error('Subscription error:', err);
      setError(err.message || 'Failed to process payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Success screen
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">You're All Set!</h2>
          <p className="text-slate-600 mb-6">
            Your subscription is now active. Your customers can start checking in digitally.
          </p>
          
          <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
            <h3 className="font-semibold text-slate-700 mb-2">Your Intake URL:</h3>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={`${window.location.origin}/s/${shopId}/intake`}
                readOnly
                className="flex-1 px-3 py-2 bg-white border border-slate-300 rounded-lg font-mono text-sm"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/s/${shopId}/intake`);
                }}
                className="px-3 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-all text-sm font-medium"
              >
                Copy
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Share this link with your customers or display it on a tablet in your waiting area.
            </p>
          </div>

          <button
            onClick={() => navigate('/dashboard')}
            className="w-full py-3 bg-slate-800 text-white rounded-xl hover:bg-slate-700 transition-all font-semibold"
          >
            Go to Dashboard →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-sm border-b border-slate-700">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-white tracking-tight">
                <span className="text-amber-400">⚡</span> The Authorization Hook
              </h1>
              <p className="text-xs text-slate-400">Complete Your Setup</p>
            </div>
            <div className="flex items-center space-x-1">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`w-10 h-1 rounded-full transition-all ${
                    s < step ? 'bg-emerald-500' : s === step ? 'bg-amber-400' : 'bg-slate-700'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        
        {/* Step 1: Business Information */}
        {step === 1 && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-4">
              <div className="flex items-center">
                <span className="w-8 h-8 rounded-full bg-amber-400 text-slate-900 flex items-center justify-center font-bold text-sm mr-3">1</span>
                <h2 className="text-lg font-semibold text-white">Business Information</h2>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Business Name</label>
                <input
                  type="text"
                  name="businessName"
                  value={businessInfo.businessName}
                  onChange={handleBusinessInfoChange}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Owner Name</label>
                <input
                  type="text"
                  name="ownerName"
                  value={businessInfo.ownerName}
                  onChange={handleBusinessInfoChange}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                  placeholder="John Smith"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={businessInfo.email}
                    onChange={handleBusinessInfoChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={businessInfo.phone}
                    onChange={handleBusinessInfoChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                  />
                </div>
              </div>
              <button
                onClick={() => setStep(2)}
                disabled={!businessInfo.businessName || !businessInfo.ownerName || !businessInfo.email}
                className="w-full py-3 bg-amber-400 text-slate-900 font-semibold rounded-lg hover:bg-amber-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Plan Selection */}
        {step === 2 && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-4">
              <div className="flex items-center">
                <span className="w-8 h-8 rounded-full bg-amber-400 text-slate-900 flex items-center justify-center font-bold text-sm mr-3">2</span>
                <h2 className="text-lg font-semibold text-white">Choose Your Plan</h2>
              </div>
            </div>
            <div className="p-6">
              {/* Single Plan Card */}
              <div className="border-2 border-amber-400 rounded-2xl p-6 bg-amber-50/50 relative">
                <div className="absolute -top-3 left-4 px-3 py-1 bg-amber-400 text-slate-900 text-xs font-bold rounded-full">
                  RECOMMENDED
                </div>
                
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-800">Pro Plan</h3>
                    <p className="text-slate-600">Everything you need to digitize your intake</p>
                  </div>
                  <div className="text-right">
                    <p className="text-4xl font-bold text-slate-800">$50</p>
                    <p className="text-slate-500">/month</p>
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  {[
                    'Unlimited customer check-ins',
                    'Real-time advisor dashboard',
                    '50-state compliant authorization',
                    'Digital signatures (ESIGN compliant)',
                    'Photo capture for plates & VINs',
                    'One-click copy to SMS systems',
                    'TCPA consent management',
                    'Email & SMS notifications',
                    'Priority support'
                  ].map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-3">
                      <svg className="w-5 h-5 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Trial Note */}
              {(shopData?.status === 'trial' || shopData?.subscriptionStatus === 'beta_trial') && shopData?.trialEndsAt && (
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-start space-x-3">
                    <svg className="w-6 h-6 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="font-semibold text-blue-800">
                        {shopData?.subscriptionStatus === 'beta_trial' ? 'Beta Access Active' : 'Trial Period Active'}
                      </p>
                      <p className="text-sm text-blue-600">
                        Your {shopData?.subscriptionStatus === 'beta_trial' ? 'beta' : 'trial'} ends {shopData.trialEndsAt.toDate ? shopData.trialEndsAt.toDate().toLocaleDateString() : new Date(shopData.trialEndsAt).toLocaleDateString()}. 
                        Set up billing now to ensure uninterrupted service.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 flex space-x-3">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 py-3 bg-slate-200 text-slate-700 font-semibold rounded-lg hover:bg-slate-300 transition-all"
                >
                  ← Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  className="flex-1 py-3 bg-amber-400 text-slate-900 font-semibold rounded-lg hover:bg-amber-500 transition-all"
                >
                  Continue to Payment →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Payment */}
        {step === 3 && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-4">
              <div className="flex items-center">
                <span className="w-8 h-8 rounded-full bg-amber-400 text-slate-900 flex items-center justify-center font-bold text-sm mr-3">3</span>
                <h2 className="text-lg font-semibold text-white">Payment Details</h2>
              </div>
            </div>
            <div className="p-6">
              {/* Order Summary */}
              <div className="bg-slate-50 rounded-xl p-4 mb-6">
                <h3 className="font-semibold text-slate-700 mb-3">Order Summary</h3>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Pro Plan (Monthly)</span>
                  <span className="font-semibold text-slate-800">$50/mo</span>
                </div>
                <div className="border-t border-slate-200 mt-3 pt-3 flex items-center justify-between">
                  <span className="font-semibold text-slate-800">Total Due Today</span>
                  <span className="text-xl font-bold text-slate-800">$50</span>
                </div>
              </div>

              {/* Square Payment Form */}
              <div className="mb-6">
                <PaymentForm
                  applicationId={SQUARE_APP_ID}
                  locationId={SQUARE_LOCATION_ID}
                  cardTokenizeResponseReceived={handleCardNonce}
                >
                  <CreditCard
                    buttonProps={{
                      isLoading: loading,
                      css: {
                        backgroundColor: '#f59e0b',
                        color: '#1e293b',
                        fontWeight: '600',
                        fontSize: '16px',
                        padding: '12px 20px',
                        borderRadius: '12px',
                        '&:hover': {
                          backgroundColor: '#d97706'
                        }
                      }
                    }}
                  >
                    {loading ? 'Processing...' : 'Start Subscription - $50/month'}
                  </CreditCard>
                </PaymentForm>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                  <div className="flex items-start space-x-3">
                    <svg className="w-6 h-6 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="font-semibold text-red-800">Payment Failed</p>
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={() => setStep(2)}
                className="w-full py-3 bg-slate-200 text-slate-700 font-semibold rounded-lg hover:bg-slate-300 transition-all"
              >
                ← Back to Plan
              </button>

              {/* Security Note */}
              <div className="mt-6 flex items-center justify-center space-x-2 text-xs text-slate-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>Secured by Square. Your card details never touch our servers.</span>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ShopOnboarding;
